import {
  Box, Typography, Stack, Chip, Grid, alpha, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExpandMore } from '@mui/icons-material';
import {
  ADMIN_STAFF_META,
  FOOD_DELIVERY_HUB_TABS,
  TRANSPORT_HUB_TABS,
  getCapabilitySections,
  type AdminStaffRole,
  type RoleCapability,
} from '../../constants/adminStaffReference';

interface AdminStaffReferenceGuideProps {
  role: AdminStaffRole;
  /** Show role capability lists (default true) */
  showCapabilities?: boolean;
  /** Show hub tab reference tables */
  showHubTabs?: boolean;
  /** Show only one hub's tab table */
  hubTabFocus?: 'transport' | 'food';
  compact?: boolean;
  /** Accordion starts expanded (dashboard / dedicated page) */
  defaultExpanded?: boolean;
  /** Hide accordion chrome — always show content */
  embedded?: boolean;
}

function CapabilityList({ title, items, accent }: { title: string; items: RoleCapability[]; accent: string }) {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: accent }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>
        {items.map((cap) => (
          <Box
            key={`${cap.code}-${cap.label}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              px: 1.5,
              py: 0.85,
              borderRadius: 1.5,
              bgcolor: alpha(accent, 0.06),
              border: 1,
              borderColor: alpha(accent, 0.15),
            }}
          >
            <Chip
              label={cap.code}
              size="small"
              sx={{
                minWidth: 32,
                height: 24,
                fontWeight: 800,
                fontSize: '0.7rem',
                bgcolor: alpha(accent, 0.15),
                color: accent,
              }}
            />
            {cap.path ? (
              <Button
                variant="text"
                size="small"
                onClick={() => navigate(cap.path!)}
                sx={{ p: 0, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', flex: 1 }}
              >
                <Typography variant="body2" sx={{ textTransform: 'none' }}>{cap.label}</Typography>
              </Button>
            ) : (
              <Typography variant="body2" flex={1}>{cap.label}</Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function HubTabsTable({ title, rows }: { title: string; rows: typeof TRANSPORT_HUB_TABS }) {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Tab</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>What it shows</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Key Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.tab}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(row.path)}
              >
                <TableCell>
                  <Chip label={row.tab} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.8125rem' }}>{row.whatItShows}</TableCell>
                <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{row.keyActions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

function ReferenceContent({
  role, showCapabilities, showHubTabs, hubTabFocus, compact,
}: Pick<AdminStaffReferenceGuideProps, 'role' | 'showCapabilities' | 'showHubTabs' | 'hubTabFocus' | 'compact'>) {
  const sections = getCapabilitySections(role);
  const showTransportTabs = showHubTabs && !compact && role !== 'employee' && (!hubTabFocus || hubTabFocus === 'transport');
  const showFoodTabs = showHubTabs && !compact && role !== 'employee' && (!hubTabFocus || hubTabFocus === 'food');
  const hasCapabilities = showCapabilities && sections.length > 0;
  const hasHubTabs = showTransportTabs || showFoodTabs;

  return (
    <Box id="admin-function-reference">
      {hasCapabilities && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ADMIN_STAFF_META.subtitle}
          </Typography>
          <Grid container spacing={2}>
            {sections.map((section) => (
              <Grid item xs={12} md={sections.length > 1 ? 6 : 12} key={section.title}>
                <CapabilityList title={section.title} items={section.items} accent={section.accent} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!hasCapabilities && hasHubTabs && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Hub tab reference — what each service tab shows and key actions.
        </Typography>
      )}

      {showTransportTabs && (
        <HubTabsTable title="Admin Hub Tabs — Transport Service" rows={TRANSPORT_HUB_TABS} />
      )}
      {showFoodTabs && (
        <HubTabsTable title="Admin Hub Tabs — Food / Delivery Service" rows={FOOD_DELIVERY_HUB_TABS} />
      )}

      {!hasCapabilities && !hasHubTabs && (
        <Typography variant="body2" color="text.secondary">No reference sections configured for this view.</Typography>
      )}
    </Box>
  );
}

export default function AdminStaffReferenceGuide({
  role,
  showCapabilities = true,
  showHubTabs = true,
  hubTabFocus,
  compact,
  defaultExpanded = false,
  embedded = false,
}: AdminStaffReferenceGuideProps) {
  if (embedded) {
    return (
      <ReferenceContent
        role={role}
        showCapabilities={showCapabilities}
        showHubTabs={showHubTabs}
        hubTabFocus={hubTabFocus}
        compact={compact}
      />
    );
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px !important',
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          px: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
          '& .MuiAccordionSummary-content': { my: 1.25 },
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>{ADMIN_STAFF_META.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            Role capabilities · hub tabs · quick navigation
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <ReferenceContent
          role={role}
          showCapabilities={showCapabilities}
          showHubTabs={showHubTabs}
          hubTabFocus={hubTabFocus}
          compact={compact}
        />
      </AccordionDetails>
    </Accordion>
  );
}
